import {
  WORKER,
  WORKER_PAYROLL,
  WRK_CATEGORY,
  WRK_DESIGNATION,
  WRK_GRADE,
  WRK_CODE_CONFIG,
  WRK_WORK_TYPE,
  WRK_LABOR_TYPE,
  WRK_MAJOR_PRODUCT,
  WRK_MACHINE,
  WRK_LEAVE_TYPE,
  WRK_LEAVE_FOR_WORKER,
  WRK_ATTENDANCE_FOR_WORKER,
  WRK_WORKER_ATTENDANCE,
  WRK_WORKER_LEAVE,
  LW_MY_REQUESTS,
  LW_PENDING_APPROVALS,
  WRK_APPROVAL_WORKFLOW,
  WRK_DAILY_OUTPUT,
  WRK_PIECE_RATE,
  WRK_WAGE_SETTINGS,
  WRK_WAGE_SHEET,
  WRK_ADVANCE,
  WRK_RPT_EARNINGS,
  WRK_RPT_OVERTIME,
  WRK_RPT_PRODUCTION,
  WRK_RPT_UNPAID,
} from '@constants/internal-route.constant'
import {useT} from 'utility/useT'
import {SidebarMenuItem} from './SidebarMenuItem'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {APPROVAL_ROUTES, LEAVE_ATTN_ROUTES, REPORT_ROUTES, SETUP_ROUTES, WAGES_ROUTES, WORKERS_ROUTES} from './menu-routes'

// The whole labor navigation: five groups. Children are `gated` (hidden without the feature
// grant), each group is hidden when none of its children survive (menu-routes.ts), and a group's
// pages are listed A–Z by their displayed label (SidebarMenuItemWithSub), so source order here
// does not matter.
//
// A group's `to` is only its open/closed key: every screen shares the /labor-welfare url prefix
// (the ACL keys), so a group is active by its children's urls (SidebarMenuItemWithSub `anyOf`).
export const SidebarMenuMain = () => {
  const t = useT()

  return (
    <>
      <SidebarMenuItemWithSub
        to='lw-workers'
        title={t('MENU.LW_G_WORKERS')}
        fontIcon='bi-people'
        anyOf={WORKERS_ROUTES}
        defaultOpen
      >
        <SidebarMenuItem gated fontIcon='bi-person-badge' to={WORKER} title={t('MENU.LW_WORKER')} />
        <SidebarMenuItem
          gated
          fontIcon='bi-cash-coin'
          to={WORKER_PAYROLL}
          title={t('MENU.LW_WORKER_PAYROLL')}
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='lw-leave-attn'
        title={t('MENU.LW_G_LEAVE_ATTN')}
        fontIcon='bi-calendar-week'
        anyOf={LEAVE_ATTN_ROUTES}
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-box-arrow-right'
          to={WRK_LEAVE_TYPE}
          title={t('MENU.LW_LEAVE_TYPE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-calendar-x'
          to={WRK_LEAVE_FOR_WORKER}
          title={t('MENU.LW_LEAVE_FOR_WORKER')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-fingerprint'
          to={WRK_ATTENDANCE_FOR_WORKER}
          title={t('MENU.LW_ATTENDANCE_FOR_WORKER')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-list-stars'
          to={WRK_WORKER_ATTENDANCE}
          title={t('MENU.LW_WORKER_ATTENDANCE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-calendar2-week'
          to={WRK_WORKER_LEAVE}
          title={t('MENU.LW_WORKER_LEAVE')}
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='lw-wages'
        title={t('MENU.LW_G_WAGES')}
        fontIcon='bi-wallet2'
        anyOf={WAGES_ROUTES}
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-clipboard-data'
          to={WRK_DAILY_OUTPUT}
          title={t('MENU.LW_DAILY_OUTPUT')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-receipt'
          to={WRK_WAGE_SHEET}
          title={t('MENU.LW_WAGE_SHEET')}
        />
        <SidebarMenuItem gated fontIcon='bi-cash-stack' to={WRK_ADVANCE} title={t('MENU.LW_ADVANCE')} />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='lw-reports'
        title={t('MENU.LW_G_REPORTS')}
        fontIcon='bi-bar-chart-line'
        anyOf={REPORT_ROUTES}
      >
        <SidebarMenuItem gated fontIcon='bi-wallet' to={WRK_RPT_EARNINGS} title={t('MENU.LW_RPT_EARNINGS')} />
        <SidebarMenuItem gated fontIcon='bi-clock-history' to={WRK_RPT_OVERTIME} title={t('MENU.LW_RPT_OVERTIME')} />
        <SidebarMenuItem gated fontIcon='bi-box-seam' to={WRK_RPT_PRODUCTION} title={t('MENU.LW_RPT_PRODUCTION')} />
        <SidebarMenuItem gated fontIcon='bi-hourglass-split' to={WRK_RPT_UNPAID} title={t('MENU.LW_RPT_UNPAID')} />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='lw-approvals'
        title={t('MENU.LW_G_APPROVALS')}
        fontIcon='bi-check2-square'
        anyOf={APPROVAL_ROUTES}
      >
        <SidebarMenuItem gated fontIcon='bi-send' to={LW_MY_REQUESTS} title={t('MENU.LW_MY_REQUESTS')} />
        <SidebarMenuItem
          gated
          fontIcon='bi-inbox'
          to={LW_PENDING_APPROVALS}
          title={t('MENU.LW_PENDING_APPROVALS')}
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='lw-setup'
        title={t('MENU.LW_G_SETUP')}
        fontIcon='bi-gear'
        anyOf={SETUP_ROUTES}
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-diagram-3'
          to={WRK_APPROVAL_WORKFLOW}
          title={t('MENU.LW_APPROVAL_WORKFLOW')}
        />
        <SidebarMenuItem gated fontIcon='bi-tags' to={WRK_CATEGORY} title={t('MENU.LW_CATEGORY')} />
        <SidebarMenuItem
          gated
          fontIcon='bi-award'
          to={WRK_DESIGNATION}
          title={t('MENU.LW_DESIGNATION')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-bar-chart-steps'
          to={WRK_GRADE}
          title={t('MENU.LW_GRADE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-stopwatch'
          to={WRK_WORK_TYPE}
          title={t('MENU.LW_WORK_TYPE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-tools'
          to={WRK_LABOR_TYPE}
          title={t('MENU.LW_LABOR_TYPE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-box-seam'
          to={WRK_MAJOR_PRODUCT}
          title={t('MENU.LW_MAJOR_PRODUCT')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-gear-wide-connected'
          to={WRK_MACHINE}
          title={t('MENU.LW_MACHINE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-upc'
          to={WRK_CODE_CONFIG}
          title={t('MENU.LW_WORKER_CODE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-tags-fill'
          to={WRK_PIECE_RATE}
          title={t('MENU.LW_PIECE_RATE')}
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-sliders'
          to={WRK_WAGE_SETTINGS}
          title={t('MENU.LW_WAGE_SETTINGS')}
        />
      </SidebarMenuItemWithSub>
    </>
  )
}
